import { Entity } from '../../entity';
import { EntityViewDisplay, EntityViewSortDirection, EntityViewType, EntityViewVisibility } from './enums';
import { ActivityEntityName } from '../../shared/activity.entity.name';
import { Lex } from '../../shared/lexorank';
import { DomainType } from '../../../utilities/domain.utility';
import { MetadataEntityFieldData } from '../../../metadata/entity/decorators/model/metadata.entity.field.data';
import { MetadataEntityRecordScope } from '../../../metadata/entity/decorators/model/metadata.entity.record.scope';
import { EntityAdvancedFilterGroup, EntityAdvancedFilterTargetStrategy } from './entity.view.advanced.filter';
import { EntityViewFilter, EntityRawViewFilter } from './entity.view.filter';
import { EntityViewGroupHeader } from './entity.view.group.header';
import { EntityViewCalendar } from './entity.view.calendar';
import { EntityViewGantt } from './entity.view.gantt';
import { FlattenType } from '../../../types/flatten.type'

export interface EntityViewHighlight<T = any> {
    _id: FlattenType<T>;
    days: number;
}

export interface EntityViewSort<T = any> {
    _id: FlattenType<T>;
    direction: EntityViewSortDirection;
}

/**
 * @deprecated Use MetadataEntityRecordScope from `metadata/entity/decorators/model`.
 * Kept as a runtime alias so existing imports keep working — new code should import the canonical enum.
 *
 * Views may also use an absolute embedded path as the grain (e.g. `workOrders.codes`) for n-level
 * secondaryFilter chains. Prefer MetadataEntityRecordExtendedScope for new code.
 */
export const EntityViewRecordScope = MetadataEntityRecordScope;
export type EntityViewRecordScope = MetadataEntityRecordScope | (string & {});

export interface EntityViewField<T = any> {
    _id: FlattenType<T>;
    attributeFilterName?: string;
    /** Storage path the board addresses the field by; differs from _id for custom fields (mapping id vs customFields.<fieldId>). */
    path?: string;
    name: string;
    systemHidden: boolean;
    visible: boolean;
}

export interface EntityViewSummary extends Entity {
    isDefault: boolean;
    /** Per-user response metadata; independent of the system default and never saved on the view. */
    isFavorite?: boolean;
    canEdit: boolean;
    selected: boolean;
    shadowed?: boolean;
    viewSortOrder?: Lex;
}

export interface EntityViewCard<T = any> {
    _id: string;
    name: string;

    identifier: FlattenType<T>;
    title: FlattenType<T>;
    description: FlattenType<T>;
    status: FlattenType<T>;
    body: FlattenType<T>[];
    footer: FlattenType<T>[];

    highlight: EntityViewHighlight;
}

export type EntityViewCardFieldMapped<T = any> = {
    [K in keyof EntityViewCard<T>]:
    EntityViewCard<T>[K] extends FlattenType<T> ? MetadataEntityFieldData
    : EntityViewCard<T>[K] extends FlattenType<T>[] ? MetadataEntityFieldData[]
    : EntityViewCard<T>[K];
};

export interface EntityViewBoard<T = any> {
    _id: string;

    card: EntityViewCard<T>;

    column: FlattenType<T>;
    row: FlattenType<T>;

    sortBy: FlattenType<T>;
    sortOrder: EntityViewSortDirection;
}

export interface EntityViewCard2<T = any> {
    _id: string;

    card: EntityViewCard<T>;

    sortBy: FlattenType<T>;
    sortOrder: EntityViewSortDirection;
}

/**
 * One kanban column on a view.
 *
 * A column is a COMBINATION of the column field's values, not necessarily one value: an operator works
 * the open queue status by status but only watches the rest, and a tenant that authored four "On Hold"
 * reasons does not want four columns of parked work. Which values belong together is a view decision,
 * so it lives here as data rather than as a rule compiled into the board.
 *
 * Exactly one of `values` / `rule` is set. `values` names the keys explicitly and is frozen — a status
 * added later lands nowhere until someone edits the view. `rule` follows a grouping attribute declared
 * in the field's metadata (`groupValue`), so a new tenant status carrying that group value joins
 * its column automatically, which is what makes a seeded system view survive tenant configuration.
 */
export interface EntityViewKanbanColumn {
    /** Stable key for the column. Any unique string within the view; the board never parses it. */
    id: string;
    /** Header label. Absent = the single value's name, or `<group value>: All` for a rule column. */
    label?: string;
    color?: string;
    /** Explicit value keys combined into this column. */
    values?: string[];
    /**
     * Rule over a value-set grouping attribute declared in field metadata (`groupValue`); follows
     * new values automatically. `expand: true` emits one column per value in the group (in the value
     * set's order) instead of combining them, which is how the open queue stays status-by-status.
     */
    rule?: { groupBy: string; value: string; expand?: boolean };
}

export interface EntityViewKanban<T = any> {
    _id: string;

    card: EntityViewCard<T>;

    column: FlattenType<T>;
    row: FlattenType<T>;

    /**
     * Ordered column definitions. Absent or empty = the default generator (one column per value in the
     * field's value-set order). A value covered by no column is NOT rendered — omission is how a view
     * drops a status from the board, so the set is exhaustive by intent, not by accident.
     */
    columns?: EntityViewKanbanColumn[];

    /**
     * Per-view override of the row field's `groupHeaderDefaults`, for the swimlane header of a
     * kanban row (job number · site · customer · count). Deliberately the same type as
     * `table.groupHeader`: a Job swimlane and a Job group row describe the same parent, so sharing
     * the shape is what stops the two displays drifting into different answers for one entity.
     * Elevated fields need not be card fields but must be projected (see collectGroupHeaderFieldIds).
     */
    rowHeader?: EntityViewGroupHeader;

    sortBy: FlattenType<T>;
    sortOrder: EntityViewSortDirection;
}

export interface EntityViewList<T = any> {
    _id: string;

    /** Left structural slots — not Linear "Display properties" chips. */
    identifier: FlattenType<T>;
    title: FlattenType<T>;
    status: FlattenType<T>;
    /**
     * Right-side chips (Linear "Display properties"). Order is left-to-right on the row.
     * Person lookups render as trailing avatars in the list row renderer.
     */
    fields: FlattenType<T>[];

    /** Maximum title lines in compact board layouts. Regular list rows remain single-line. */
    titleLines?: 1 | 2 | 3;

    groupBy: FlattenType<T>;
    subGroupBy: FlattenType<T>;

    sortBy: FlattenType<T>;
    sortOrder: EntityViewSortDirection;

    /**
     * When grouped, show empty group buckets. Only honoured when the board opts in via
     * `board.list.showEmptyGroups` and groupBy has a known value set.
     */
    showEmptyGroups?: boolean;
    /** Show rows whose direct parent differs from the board's scoped parent. */
    showNestedItems?: boolean;
    /** Visually indent those nested rows while keeping their existing list order. */
    indentNestedItems?: boolean;
}

export interface EntityViewTable<T = any> {
    _id: string;

    fixed: EntityViewField<T>[];
    other: EntityViewField<T>[];

    highlight: EntityViewHighlight;

    /** Optional group by field for list views that support grouping (like items dashboard) */
    groupBy?: FlattenType<T>;
    /** Optional sub-group by field for nested grouping */
    subGroupBy?: FlattenType<T>;
    /**
     * Optional chrome for the AG Grid group row when groupBy is set.
     * Elevated fields need not be visible columns but must be projected (see collectGroupHeaderFieldIds).
     */
    groupHeader?: EntityViewGroupHeader;
    /**
     * `groupBy` groups are read from the server rather than folded client-side over one page: the
     * pager pages whole groups and a group's rows load in blocks as it opens (see `EntityItemsGroupRow`).
     */
    serverGroups?: EntityViewServerGroups;
}

export interface EntityViewServerGroups {
    /** The one `groupBy` field the reader groups; any other choice stays client-side over the page. */
    field: string;
    /** Plural noun the pager counts in ("plans"). */
    unit: string;
}

export interface EntityViewPreset<T = any> {
    _id: string;
    label: string;
    color?: string;
    /** Selected on load when no preset is chosen. First one wins; absent on all = no filter (All). */
    isDefault?: boolean;
    /** Empty/absent = the "All" preset (no extra filter). Reuses the standard view-filter format. */
    filters?: EntityViewFilter<T>[];
}

/** How a view's presets are rendered. Discriminated by `type` so each style carries its own config;
 *  add a new style as another union member (non-breaking). Absent = tabs with default config. */
export interface EntityViewPresetTabsDisplay {
    type: 'tabs';
    /** Show per-preset count badges on each tab. Default true. */
    showCount?: boolean;
}

export type EntityViewPresetDisplay = EntityViewPresetTabsDisplay;

/** A view's preset feature config: the preset bundles + how they render. */
export interface EntityViewPresetConfig<T = any> {
    items: EntityViewPreset<T>[];
    /** How presets render + per-style config. Absent = tabs with defaults. */
    display?: EntityViewPresetDisplay;
}

/** Single source for the `display` semantics: should per-preset counts be requested/shown.
 *  Shared by the dashboard (gates the count $facet fetch) and the presets component (badge render),
 *  so a new display member is interpreted in one place, not duplicated per consumer. */
export const presetCountsEnabled = (config?: EntityViewPresetConfig): boolean =>
    !!config?.items?.length
    && (config.display?.type ?? 'tabs') === 'tabs'
    && (config.display?.showCount ?? true);

export interface EntityView<T = any> extends EntityViewSummary {

    entity: ActivityEntityName;
    type: EntityViewType;

    visibility: EntityViewVisibility;
    sharedWith?: Entity[];

    display: EntityViewDisplay;
    /** v2 board: render the document preview pane beside the collection (Table → split, Card →
     *  focus rail). Orthogonal to `display`; only honoured when the entity supports preview
     *  (isFinancialDocument). Absent = true. The detail-flavoured displays (CardDetail/ListDetail/
     *  TableDetail) are legacy aliases normalized to base display + showPreview by the v2 board. */
    showPreview?: boolean;
    pageSize?: number;
    recordScope?: EntityViewRecordScope;

    /** @deprecated Highlighting is now done in the list or board settings */
    highlight: EntityViewHighlight;

    /** This is mean to allow you to specify a mongodb type filter. We should ensure the view is a system view before attempting to process */
    rawFilters?: EntityRawViewFilter<T>[];

    /** @deprecated Use advancedFilter instead */
    filters: EntityViewFilter<T>[];
    advancedFilter?: EntityAdvancedFilterGroup<T>;
    advancedFilterStrategy?: EntityAdvancedFilterTargetStrategy;

    /** @deprecated Sorting is now done in the table or board settings */
    sort: EntityViewSort<T>;

    /** @deprecated use v2 */
    card?: EntityViewCard<T>;
    card2?: EntityViewCard2<T>;
    board: EntityViewBoard<T>;
    kanban?: EntityViewKanban<T>;
    list: EntityViewList<T>;
    table: EntityViewTable<T>;
    /** Calendar display configuration; present when `display` is Calendar. */
    calendar?: EntityViewCalendar<T>;
    gantt?: EntityViewGantt;

    quickFilters: FlattenType<T>[];
    hiddenFilters?: FlattenType<T>[];
    presetConfig?: EntityViewPresetConfig<T>;

    domain?: DomainType;
    variant?: string;
    viewSortOrder?: Lex;

    /**
     * Board-owned display-settings contribution bag (namespaced by contribution id).
     * Not persisted on the server view document — layered by the v2 board from session/localStorage
     * and forwarded on items requests as `preview.extensions`. The shell never interprets keys.
     *
     * @example `{ 'file-scope': { showAll: true, sharing: 'All' } }`
     */
    extensions?: Record<string, unknown>;
}

/** v2 board display normalization: the detail-flavoured displays are legacy aliases — the board
 *  renders base displays with preview as an orthogonal `showPreview` flag. Applied at the v2 board
 *  boundary ONLY; v1 dashboards keep reading the raw display, so shared system views stay intact. */
export function normalizeViewDisplayForBoard<T = any>(view: EntityView<T>): EntityView<T> {

    switch (view?.display) {
        case EntityViewDisplay.CardDetail:
        case EntityViewDisplay.ListDetail:
            return { ...view, display: EntityViewDisplay.Card, showPreview: view.showPreview ?? true };
        case EntityViewDisplay.TableDetail:
            return { ...view, display: EntityViewDisplay.Table, showPreview: view.showPreview ?? true };
        default:
            return view;
    }
}

export interface EntityViewRequestOptions {
    userId?: string,
    variant?: string,
    domainType?: DomainType,
    type?: EntityViewType
}

export interface EntityViewGroupingOptions {
    groupEnabled: boolean;
    subGroupEnabled: boolean;
}

export interface EntityViewDefaults<T = any> {

    entity: ActivityEntityName;
    type: EntityViewType;

    visibility: EntityViewVisibility;
    visibilityEnabled: boolean;

    display: EntityViewDisplay;
    displayEnabled: boolean;

    card?: EntityViewCard<T> & EntityViewGroupingOptions;
    board: EntityViewBoard<T> & EntityViewGroupingOptions;
    kanban?: EntityViewKanban<T> & EntityViewGroupingOptions;
    list: EntityViewList<T> & EntityViewGroupingOptions;
    table: EntityViewTable<T> & EntityViewGroupingOptions;
    calendar?: EntityViewCalendar<T>;
    gantt?: EntityViewGantt;

    filters: EntityViewFilter<T>[];

    quickFilters: FlattenType<T>[];
    hiddenFilters?: FlattenType<T>[];
}


export type EntityViewResolver = () => EntityView;
